import React, { useContext, useMemo } from 'react';
import { IWidgetContentProps } from '../../../../../Dashboard/Components/Widget/Widget';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import { useActiveDBCollectionQuery } from '../../../../../../Components/QueryComponents/ActiveDBCollectionQuery/ActiveDBCollectionQuery';
import { useQuery } from '@apollo/client';
import defaultColumns from '../../../../_Common/Utils/defaultColumns';
import { addAllDataDependencies } from '../../../../_Common/Const/BusinessConfigs';
import { getEmptyUrval } from '../../../../../../Components/Urval/Utils/urvalUtils';
import { rutinInfos } from '../../../../_Common/Const/RutinInfo';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { ICollectionDataType } from '../../../../_Common/Components/BusinessCollectionComponents/Queries/BusinessCollectionQuery';
import { getTodaysDate } from '../../../../../../Utils/utils';
import EDSkeleton from '../../../../../../BaseComponents/Skeleton/EDSkeleton';
import { findCollectionStatic } from '../../../../../../Model/avenyStaticModelUtils';
import { TEDSelectionOperator } from '../../../../../../globalQueryTypes';
import {
  INumericConditionObject,
  IUIUrval,
} from '../../../../../../Types/UrvalTypes';
import {
  compareDateTime,
  getMonthText,
  tryDateTimeStringToDateTime,
} from '../../../../../../Utils/dateUtils';
import mergeSort from '../../../../../../Utils/mergeSort';
import RouterLink from '../../../../../../Components/RouterLink/RouterLink';
import {
  buildBasePath,
  useBaseRouteValue,
} from '../../../../../Routing/Utils/routerUtils';
import { LoginStateContext } from '../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import meQuery from '../Queries/MeQuery/MeQuery.gql';
import {
  MeQuery,
  MeQueryVariables,
} from '../Queries/MeQuery/_QueryTypes/MeQuery';
import resursQuery from '../Queries/ResursQuery/ResursQuery.gql';
import {
  ResursQuery,
  ResursQueryVariables,
} from '../Queries/ResursQuery/_QueryTypes/ResursQuery';
import {
  getDefaultBookingGroupName,
  getLoggedInResource,
} from '../Utils/ResourceUtils';
import { themeColors } from '../../../../../../BaseComponents/ThemeProvider/AvenyThemeColors';

const DATE_TIME_SPLIT_REGEX = /[ T]/;
const DATE_TIME_CLEAN_REGEX = /[-: T]/g;

const parseDate = (dateString?: string): Date | null => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatDate = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')}`;
};

const getAktivitet = (row: any) => row?._Associations?.Aktivitet;

const getRowDatePart = (row: any): string => {
  return getDatePartFromDateTime(row?.FranDatumTid ?? row?.FRANDATUM);
};

const getRowDateTime = (row: any): string => {
  return row?.FranDatumTid ?? `${row?.FRANDATUM ?? ''} ${row?.FRANTID ?? ''}`;
};

const getBookingStartDate = (row: any): string | undefined => {
  return row?.OriginalFranDatum ?? row?.FRANDATUM;
};

const getBookingEndDate = (row: any): string | undefined => {
  return row?.OriginalTillDatum ?? row?.TILLDATUM;
};

const isFlerdagsbokning = (row: any): boolean => {
  const bookingStartDate = getBookingStartDate(row);
  const bookingEndDate = getBookingEndDate(row);

  return (
    !!bookingStartDate &&
    !!bookingEndDate &&
    bookingStartDate !== bookingEndDate
  );
};

const expandMultiDayRows = (rows: any[]): any[] => {
  return rows.flatMap(row => {
    if (!isFlerdagsbokning(row)) return [row];

    const fromDate = row?.FRANDATUM;
    const toDate = row?.TILLDATUM;
    if (!fromDate || !toDate || fromDate === toDate) return [row];

    const start = parseDate(fromDate);
    const end = parseDate(toDate);
    if (!start || !end || start > end) return [row];

    const perDayRows: any[] = [];
    for (
      let current = new Date(start);
      current <= end;
      current.setDate(current.getDate() + 1)
    ) {
      const datePart = formatDate(current);
      perDayRows.push({
        ...row,
        OriginalFranDatum: fromDate,
        OriginalTillDatum: toDate,
        FRANDATUM: datePart,
        TILLDATUM: datePart,
        FranDatumTid: `${datePart} ${row?.FRANTID ?? '00:00'}`,
        TillDatumTid: `${datePart} ${row?.TILLTID ?? '23:59'}`,
      });
    }

    return perDayRows;
  });
};

const sortExpandedRows = (rows: any[]): any[] => {
  return mergeSort([...rows], (a, b) => {
    const aDateTime = getRowDateTime(a);
    const bDateTime = getRowDateTime(b);
    const aActivityId = getAktivitet(a)?.id ?? '';
    const bActivityId = getAktivitet(b)?.id ?? '';

    if (aDateTime < bDateTime) return -1;
    if (aDateTime > bDateTime) return 1;
    if (aActivityId < bActivityId) return -1;
    if (aActivityId > bActivityId) return 1;
    return 0;
  });
};

export const getDatePartFromDateTime = (fromDateTime?: string): string => {
  return fromDateTime?.split(DATE_TIME_SPLIT_REGEX)[0] ?? '';
};

export const toCompactDateTimeString = (fromDateTime?: string): string => {
  return fromDateTime?.replace(DATE_TIME_CLEAN_REGEX, '') ?? '';
};

export const getKalenderBookingRoute = (
  basePath: string,
  bookingId?: string
): string => {
  if (!bookingId) return `${basePath}/Rutin/Kalender`;
  return `${basePath}/Rutin/Kalender/${bookingId}`;
};

export const getTomorrowDateString = (today: string): string => {
  const [year, month, day] = today.split('-').map(Number);
  const tomorrowDate = new Date(year, month - 1, day + 1);
  const tomorrowYear = tomorrowDate.getFullYear();
  const tomorrowMonth = (tomorrowDate.getMonth() + 1)
    .toString()
    .padStart(2, '0');
  const tomorrowDay = tomorrowDate.getDate().toString().padStart(2, '0');

  return `${tomorrowYear}-${tomorrowMonth}-${tomorrowDay}`;
};

export const getBookingDateTitle = (
  fromDateTime: string | undefined,
  today: string,
  tomorrow: string
): string => {
  const datePart = getDatePartFromDateTime(fromDateTime);
  const [, todayMonth, todayDay] = today.split('-').map(Number);

  if (!datePart || datePart === today)
    return `Idag ${todayDay} ${getMonthText(todayMonth)}`;

  const [, month, day] = datePart.split('-').map(Number);
  if (datePart === tomorrow) return `Imorgon ${day} ${getMonthText(month)}`;

  return `${day} ${getMonthText(month)}`;
};

export const shouldShowDateTitle = (
  currentFromDateTime: string | undefined,
  previousFromDateTime: string | undefined,
  index: number
): boolean => {
  const currentDatePart = getDatePartFromDateTime(currentFromDateTime);
  const previousDatePart = getDatePartFromDateTime(previousFromDateTime);
  return index === 0 || currentDatePart !== previousDatePart;
};

export const KalenderWidget: React.FC<IWidgetContentProps> = props => {
  const loginStateContext = useContext(LoginStateContext);
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const baseRouteValues = useBaseRouteValue();
  const basePath = useMemo(
    () => buildBasePath(baseRouteValues),
    [baseRouteValues]
  );
  const columns = useMemo(() => defaultColumns('BOBokningsrubrik'), []);
  const attributeDefs = useMemo(() => {
    const defs = addAllDataDependencies('BOBokningsrubrik', columns);
    return Array.from(
      new Set([
        ...defs,
        'Resurs.BO_RESURS_ID',
        'Aktivitet.REPAKTNR',
        'Aktivitet.BOKNINGSNUMMER',
        'Aktivitet.TITEL',
        'Aktivitet.FRANDATUM',
        'Aktivitet.FRANTID',
        'Aktivitet.TILLDATUM',
        'Aktivitet.TILLTID',
      ])
    );
  }, [columns]);
  const today = getTodaysDate();
  const tomorrow = useMemo(() => getTomorrowDateString(today), [today]);

  const isPastTime = (fromDateTime?: string): boolean => {
    if (!fromDateTime) return false;

    const compactDateTime = toCompactDateTimeString(fromDateTime);
    const bookingDateTime = tryDateTimeStringToDateTime(compactDateTime);
    if (!bookingDateTime) return false;

    return compareDateTime(bookingDateTime, new Date()) < 0;
  };

  const tenDaysFromToday = useMemo(() => {
    const [year, month, day] = today.split('-').map(Number);
    const d = new Date(year, month - 1, day + 10);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(d.getDate()).padStart(2, '0')}`;
  }, [today]);

  const urval = useMemo((): IUIUrval => {
    const bokningsrubrikStatic = findCollectionStatic('BOBokningsrubrik');
    const franDatumAttr = bokningsrubrikStatic?.Attributes?.['FRANDATUM'];
    const tillDatumAttr = bokningsrubrikStatic?.Attributes?.['TILLDATUM'];

    // Use overlap logic: FRANDATUM <= tenDaysFromToday AND TILLDATUM >= today.
    // This ensures multi-day F bookings that started before today are included.
    if (franDatumAttr && tillDatumAttr) {
      const franCondition: INumericConditionObject = {
        type: 'Numeric',
        id: 'FRANDATUM-overlap',
        attribute: franDatumAttr,
        condition: [
          {
            id: 'fran-cond-to',
            op: TEDSelectionOperator.notgreater,
            values: [{ id: 'fran-val-to', value: tenDaysFromToday }],
          },
        ],
      };
      const tillCondition: INumericConditionObject = {
        type: 'Numeric',
        id: 'TILLDATUM-overlap',
        attribute: tillDatumAttr,
        condition: [
          {
            id: 'till-cond-from',
            op: TEDSelectionOperator.notless,
            values: [{ id: 'till-val-from', value: today }],
          },
        ],
      };
      return {
        pages: [
          {
            id: 'kalender-page',
            conditionObjects: [franCondition, tillCondition],
          },
        ],
      };
    }

    // Fallback for collections without separate FRANDATUM/TILLDATUM attributes.
    const datumAttr =
      bokningsrubrikStatic?.Attributes?.['DATUM'] ??
      bokningsrubrikStatic?.Attributes?.['FranDatumTid'];
    if (!datumAttr) return getEmptyUrval();

    const dateCondition: INumericConditionObject = {
      type: 'Numeric',
      id: 'DATUM-today',
      attribute: datumAttr,
      condition: [
        {
          id: 'datum-cond-from',
          op: TEDSelectionOperator.notless,
          values: [{ id: 'datum-val-from', value: today }],
        },
        {
          id: 'datum-cond-to',
          op: TEDSelectionOperator.notgreater,
          values: [{ id: 'datum-val-to', value: tenDaysFromToday }],
        },
      ],
    };
    return {
      pages: [{ id: 'kalender-page', conditionObjects: [dateCondition] }],
    };
  }, [today, tenDaysFromToday]);

  const { data, loading, serverError } = useActiveDBCollectionQuery({
    routineID: props.routineInfo?.rutinID ?? rutinInfos.rtnWebKalender.rutinID,
    collectionName: 'BOBokningsrubrik',
    queryName: 'rtnWebKalenderMain',
    attributeDefs,
    urval,
    networkOnlyOnFirst: true,
  });

  const { data: meData, loading: meLoading } = useQuery<
    MeQuery,
    MeQueryVariables
  >(meQuery, {
    variables: {
      dbname: activeDatabaseContext.activeDatabase ?? '',
    },
    skip: !activeDatabaseContext.activeDatabase,
  });

  const defaultBookingGroup = useMemo(
    () => getDefaultBookingGroupName(meData),
    [meData]
  );

  const { data: resursData, loading: resursLoading } = useQuery<
    ResursQuery,
    ResursQueryVariables
  >(resursQuery, {
    variables: {
      dbname: activeDatabaseContext.activeDatabase ?? '',
      routineID:
        props.routineInfo?.rutinID ?? rutinInfos.rtnWebKalender.rutinID,
      selectedBokningsgrupp: defaultBookingGroup ?? '',
    },
    skip:
      !defaultBookingGroup ||
      !activeDatabaseContext.activeDatabase ||
      meLoading,
  });

  const loggedInResource = useMemo(
    () =>
      resursData
        ? getLoggedInResource(loginStateContext.sign, resursData)
        : undefined,
    [loginStateContext.sign, resursData]
  );

  const filteredRowsForLoggedInUser = useMemo(() => {
    const rows = (data ??
      ([] as ICollectionDataType[])) as ICollectionDataType[];
    const loggedInResourceId = loggedInResource?.BO_RESURS_ID;

    if (!loggedInResourceId) return [];

    return rows.filter(
      row => row?._Associations?.Resurs?.BO_RESURS_ID === loggedInResourceId
    );
  }, [data, loggedInResource?.BO_RESURS_ID]);

  const activity = (rows: any[]) => {
    const expanded = expandMultiDayRows(rows).filter(
      row => getRowDatePart(row) >= today
    );
    const sortedExpandedRows = sortExpandedRows(expanded);

    const MAX_RENDER_HEIGHT = 250;
    const TITLE_HEIGHT = 28;
    const ROW_HEIGHT = 24;
    let accumulatedHeight = 0;
    let lastRenderedDatePart: string | undefined;
    const renderedRows: React.ReactNode[] = [];
    const seenBookingPerDate = new Set<string>();
    const seenTimeTitleKeys = new Set<string>();
    const activityDateParts = new Map<string, Set<string>>();
    const repeatedMidnightKeys = new Set<string>();

    sortedExpandedRows.forEach(row => {
      const aktivitetId = getAktivitet(row)?.id as string | undefined;
      const datePart = getRowDatePart(row);
      if (!aktivitetId || !datePart) return;

      const dateSet = activityDateParts.get(aktivitetId) ?? new Set<string>();
      dateSet.add(datePart);
      activityDateParts.set(aktivitetId, dateSet);
    });

    sortedExpandedRows.forEach(row => {
      const aktivitet = getAktivitet(row);
      const aktivitetId = aktivitet?.id as string | undefined;
      const datePart = getRowDatePart(row);
      const repAktNr = aktivitet?.REPAKTNR;
      const rawTitle = aktivitet?.TITEL ?? '';
      const normalizedTitle = rawTitle.trim().toLowerCase();
      if (!datePart || repAktNr == null) return;

      const isMultiDayBooking = isFlerdagsbokning(row);
      const multiDayStartDate =
        getBookingStartDate(row) ?? aktivitet?.FRANDATUM ?? row?.FRANDATUM;
      const isStartDateForMultiDayBooking = datePart === multiDayStartDate;
      const hasPerDayExpansion = aktivitetId
        ? (activityDateParts.get(aktivitetId)?.size ?? 0) > 1
        : false;
      const shouldUseMidnight =
        isMultiDayBooking &&
        hasPerDayExpansion &&
        !isStartDateForMultiDayBooking;

      if (shouldUseMidnight) {
        repeatedMidnightKeys.add(`${datePart}|${repAktNr}|${normalizedTitle}`);
      }
    });

    sortedExpandedRows.forEach(row => {
      const aktivitet = getAktivitet(row);

      const aktivitetFranTid = aktivitet?.FRANTID;
      const rowFranTid = row?.FRANTID;
      const aktivitetId = aktivitet?.id as string | undefined;
      const datePart = getRowDatePart(row);
      const repAktNr = aktivitet?.REPAKTNR;

      const bookingPerDateKey = `${datePart}|${aktivitetId ?? row?.id}`;
      if (seenBookingPerDate.has(bookingPerDateKey)) return;
      seenBookingPerDate.add(bookingPerDateKey);

      const isMultiDayBooking = isFlerdagsbokning(row);
      const multiDayStartDate =
        getBookingStartDate(row) ?? aktivitet?.FRANDATUM ?? row?.FRANDATUM;
      const isStartDateForMultiDayBooking = datePart === multiDayStartDate;
      const hasPerDayExpansion = aktivitetId
        ? (activityDateParts.get(aktivitetId)?.size ?? 0) > 1
        : false;
      const shouldUseMidnight =
        isMultiDayBooking &&
        hasPerDayExpansion &&
        !isStartDateForMultiDayBooking;

      const rawTitle = aktivitet?.TITEL ?? '';
      const normalizedTitle = rawTitle.trim().toLowerCase();
      const repeatedMidnightKey = `${datePart}|${repAktNr}|${normalizedTitle}`;

      if (
        repAktNr != null &&
        datePart !== today &&
        !shouldUseMidnight &&
        repeatedMidnightKeys.has(repeatedMidnightKey)
      ) {
        return;
      }

      const displayedTime = shouldUseMidnight
        ? '00:00'
        : rowFranTid !== aktivitetFranTid
        ? `(${rowFranTid}) `
        : rowFranTid ?? aktivitetFranTid;

      const normalizedDisplayedTime = String(displayedTime ?? '').trim();
      const timeTitleKey = `${datePart}|${normalizedDisplayedTime}|${normalizedTitle}`;

      if (seenTimeTitleKeys.has(timeTitleKey)) return;
      seenTimeTitleKeys.add(timeTitleKey);

      const showTitle = lastRenderedDatePart !== datePart;

      const rowHeight = (showTitle ? TITLE_HEIGHT : 0) + ROW_HEIGHT;
      if (accumulatedHeight + rowHeight > MAX_RENDER_HEIGHT) return;

      accumulatedHeight += rowHeight;
      lastRenderedDatePart = datePart;

      const renderKey = `${row.id}-${datePart}-${normalizedDisplayedTime}-${normalizedTitle}`;

      renderedRows.push(
        <React.Fragment key={renderKey}>
          {showTitle && (
            <EDTypography
              variant="body2"
              maxHeight={'28px'}
              paddingLeft={1}
              bold
              marginBottom={0.5}
            >
              {getBookingDateTitle(getRowDateTime(row), today, tomorrow)}
            </EDTypography>
          )}
          <RouterLink
            to={getKalenderBookingRoute(
              basePath,
              row?._Associations?.Aktivitet?.id ?? row?.id
            )}
          >
            <EDTypography
              fontSize={'12px'}
              maxHeight={'24px'}
              sx={{
                transition: theme =>
                  theme.transitions.create('color', {
                    duration: theme.transitions.duration.shortest,
                  }),
                '&:hover': {
                  backgroundColor: themeColors.blueGrayFade[200.87],
                },
                color: isPastTime(getRowDateTime(row))
                  ? theme => theme.palette.text.disabled
                  : theme => theme.palette.text.primary,
                border: theme =>
                  `0.5px solid ${theme.customProperties.Fade.DarkOnLight.divider}`,
                backgroundColor: themeColors.blueGray[50],
                padding: '3px 8px',
                borderRadius: 1.5,
                maxWidth: '285px',
                marginLeft: 1,
                marginBottom: 1,
              }}
            >
              <EDBox
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayedTime} {row?._Associations?.Aktivitet?.TITEL}
              </EDBox>
            </EDTypography>
          </RouterLink>
        </React.Fragment>
      );
    });

    return renderedRows;
  };

  return loading || meLoading || resursLoading ? (
    <EDBox>
      <EDSkeleton animation="pulse" height={'50px'} />
      <EDSkeleton animation="pulse" height={'50px'} />
      <EDSkeleton animation="pulse" height={'50px'} />
      <EDSkeleton animation="pulse" height={'50px'} />
      <EDSkeleton animation="pulse" height={'50px'} />
    </EDBox>
  ) : serverError ? (
    <EDTypography>{serverError.message}</EDTypography>
  ) : filteredRowsForLoggedInUser.length === 0 ? (
    <EDTypography variant="body2" paddingLeft={1}>
      Du har inga bokningar
    </EDTypography>
  ) : (
    <EDBox
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }}
    >
      {activity(filteredRowsForLoggedInUser)}
    </EDBox>
  );
};
